import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Download, Upload, Search, X, Trash2, Save, AlertTriangle, Info, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import MonthlyAttendanceTab from './MonthlyAttendanceTab';
import SpreadsheetTable from '../../components/report-index/SpreadsheetTable';
import DailyScheduleTable from '../../components/report-index/DailyScheduleTable';
import { SUB_TABS, STATUS_OPTS, TIME_SLOTS } from '../../components/report-index/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getH = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('crm_token')}` });

const TAB_BANNERS = {
  index1: { title: 'DEPARTMENT INDEX-1', sub: 'CHECKLIST', bg: '#FFD700', fg: '#000' },
  index2: { title: 'DEPARTMENT INDEX-2', sub: 'CHECKLIST', bg: '#FFD700', fg: '#000' },
  presently: { title: 'ATTENDANCE', sub: 'Monthly Attendance', bg: '#375623', fg: '#fff' },
  daily_schedule: { title: 'DAILY SCHEDULE & WORK', sub: '', bg: '#7B3F00', fg: '#fff' },
  daily_work: { title: 'DAILY WORK', sub: '', bg: '#1F3864', fg: '#fff' },
  work_problem: { title: 'WORK PROBLEM', sub: '', bg: '#A6A6A6', fg: '#fff' },
  suggestion: { title: 'SUGGESTION', sub: '', bg: '#A6A6A6', fg: '#fff' },
  company_dev: { title: 'COMPANY DEVELOPMENT IDEA', sub: '', bg: '#A6A6A6', fg: '#fff' },
  cost_cutting: { title: 'COST CUTTING', sub: '', bg: '#A6A6A6', fg: '#fff' },
  senior_suggestion: { title: 'CHECK POINT', sub: '', bg: '#A6A6A6', fg: '#fff' },
};

const FILTERABLE_TABS = ['work_problem','suggestion','company_dev','cost_cutting','senior_suggestion'];
const IMPORTABLE_TABS = ['work_problem','suggestion','company_dev','cost_cutting','senior_suggestion'];

const parseExcelDate = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/);
  if (m) return new Date(+m[3], +m[2]-1, +m[1]);
  m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return new Date(+m[1], +m[2]-1, +m[3]);
  return null;
};

const ReportIndexTab = ({ user, isHR = false, startInAllView = false }) => {
  const [activeSubTab, setActiveSubTab] = useState('index1');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmail, setFilterEmail] = useState(isHR ? 'all' : (user?.email || ''));
  const [exporting, setExporting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [hrPeople, setHrPeople] = useState([{ label: 'All Combined', email: 'all', dept: '' }]);
  const [importing, setImporting] = useState(false);
  const [importingWb, setImportingWb] = useState(false);
  const [hrView, setHrView] = useState(startInAllView ? 'all' : 'self'); // 'self' | 'all' — only relevant when isHR=true
  const [deptFilter, setDeptFilter] = useState('ALL');
  const fileInputRef = useRef(null);
  const wbFileRef = useRef(null);
  const [saveAllTrigger, setSaveAllTrigger] = useState(0);
  const [dirtyCount, setDirtyCount] = useState(0);
  const [savingAll, setSavingAll] = useState(false);

  // ── Import confirmation modal (replaces browser window.confirm) ──
  const [importConfirm, setImportConfirm] = useState(null); // { type: 'tab'|'workbook', onConfirm }
  // ── Import Preview modal ──
  const [importPreview, setImportPreview] = useState(null); // { file, previewData }
  const [previewLoading, setPreviewLoading] = useState(false);
  // ── Sheet Name Guide popover ──
  const [showSheetGuide, setShowSheetGuide] = useState(false);
  const sheetGuideRef = useRef(null);

  // Close sheet guide on outside click
  useEffect(() => {
    if (!showSheetGuide) return;
    const handler = (e) => {
      if (sheetGuideRef.current && !sheetGuideRef.current.closest('[data-sheet-guide-container]')?.contains(e.target)) {
        setShowSheetGuide(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSheetGuide]);

  // DM/ADM/CP channel people (hierarchy accounts)
  const [channelPeople, setChannelPeople] = useState([]);
  const [channelSearch, setChannelSearch] = useState('');
  const [loadingChannel, setLoadingChannel] = useState(false);
  // Emails of employees who have already submitted index data
  const [activeSubmitters, setActiveSubmitters] = useState(new Set());

  const handleSaveAll = async () => {
    setSavingAll(true);
    setSaveAllTrigger(t => t + 1);
    setTimeout(() => { setSavingAll(false); setDirtyCount(0); }, 800);
  };

  useEffect(() => { setSearchText(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); }, [activeSubTab, filterEmail]);

  const personName = user ? `${(user.firstName||'')} ${(user.lastName||'')}`.trim().toUpperCase() : '';
  const dept = (user?.department||'').toUpperCase();
  // isHRAll: true only when HR user is in "All Employees" view mode
  const isHRAll = isHR && hrView === 'all';

  useEffect(() => {
    if (!isHR) return;
    (async () => {
      try {
        const res = await fetch(`${API}/crm/admin/users`, { headers: getH() });
        if (!res.ok) return;
        const allUsers = await res.json();
        const seenIds = new Set();
        const users = allUsers.filter(u => {
          // Exclude hierarchy placeholder DM/ADM/CP accounts (10,330 accounts) — shown separately
          if (/^(DM|ADM|CP)\d{5}$/.test(u.employeeId || '')) return false;
          if (seenIds.has(u.employeeId)) return false;   // defensive: no duplicate rows
          seenIds.add(u.employeeId);
          return true;
        });
        const opts = [
          { label: `All Combined (${users.length})`, email: 'all', dept: '' },
          ...users
            .filter(u => u.email && u.firstName)
            .sort((a, b) => `${a.firstName} ${a.lastName||''}`.localeCompare(`${b.firstName} ${b.lastName||''}`))
            .map(u => ({
              label: `${u.firstName} ${u.lastName||''}`.trim() + (u.employeeId ? ` (${u.employeeId})` : ''),
              email: u.email,
              dept: (u.department || '').toUpperCase(),
            })),
        ];
        setHrPeople(opts);
      } catch { /* silent */ }
    })();
  }, [isHR]);

  // Load employees who have already submitted index data (for ✓ marking in channel dropdown)
  useEffect(() => {
    if (!isHR) return;
    (async () => {
      try {
        const res = await fetch(`${API}/report-index/active-employees`, { headers: getH() });
        if (!res.ok) return;
        const data = await res.json();
        setActiveSubmitters(new Set((data.employees || []).map(e => e.email)));
      } catch { /* silent */ }
    })();
  }, [isHR]);

  // Channel dept constants — declared early so loadEntries callback can reference them
  const CHANNEL_DEPTS = ['DM', 'ADM', 'CP'];

  const loadEntries = useCallback(async () => {
    if (activeSubTab === 'presently') { setEntries([]); setLoading(false); return; }
    setLoading(true);
    try {
      const email = isHRAll ? filterEmail : (user?.email || '');
      const params = new URLSearchParams({ tab: activeSubTab });
      if (email && email !== 'all') params.set('email', email);
      else if (isHRAll) params.set('email', 'all');
      // When "All DM / ADM / CP Combined" is selected, pass dept_filter so only
      // that channel's entries are returned (not all staff + channel mixed)
      if (isHRAll && filterEmail === 'all' && CHANNEL_DEPTS.includes(deptFilter)) {
        params.set('dept_filter', deptFilter);
      }
      const res = await fetch(`${API}/report-index/entries?${params}`, { headers: getH() });
      const data = await res.json();
      setEntries(data.entries || []);
    } catch { toast.error('Failed to load entries'); }
    finally { setLoading(false); }
  }, [activeSubTab, filterEmail, isHRAll, user, deptFilter]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const loadChannelPeople = useCallback(async (level, search = '') => {
    if (!isHR) return;
    setLoadingChannel(true);
    try {
      const limit = level === 'CP' ? 100 : 500;
      const params = new URLSearchParams({ level, skip: 0, limit });
      if (search) params.set('search', search);
      const res = await fetch(`${API}/hierarchy-accounts/list?${params}`, { headers: getH() });
      if (!res.ok) return;
      const data = await res.json();
      const DOMAIN = 'siddhivinayakinfra.estate';
      setChannelPeople(data.items.map(p => ({
        label: `${p.id}${p.name ? ' \u2013 ' + p.name : ''}`,
        email: `${p.id.toLowerCase()}@${DOMAIN}`,
        dept: level,
      })));
    } catch { /* silent */ }
    finally { setLoadingChannel(false); }
  }, [isHR]);

  // Load when dept pill switches to a channel dept
  useEffect(() => {
    if (CHANNEL_DEPTS.includes(deptFilter)) {
      setFilterEmail('all');
      setChannelPeople([]);
      loadChannelPeople(deptFilter, '');
    }
  }, [deptFilter]); // eslint-disable-line

  // Debounced search for CP (there are 10,000 CPs so we search lazily)
  useEffect(() => {
    if (deptFilter !== 'CP') return;
    const t = setTimeout(() => loadChannelPeople('CP', channelSearch), 400);
    return () => clearTimeout(t);
  }, [channelSearch]); // eslint-disable-line

  const handleSaveRow = async (id, rowData) => {
    try {
      const res = await fetch(`${API}/report-index/entries/${id}`, {
        method: 'PUT', headers: getH(),
        body: JSON.stringify({ row_data: rowData }),
      });
      if (!res.ok) throw new Error();
      setEntries(prev => prev.map(e => e.id === id ? { ...e, row_data: rowData } : e));
    } catch { toast.error('Save failed'); }
  };

  const handleAddRow = async (rowData) => {
    try {
      const res = await fetch(`${API}/report-index/entries`, {
        method: 'POST', headers: getH(),
        body: JSON.stringify({
          tab: activeSubTab, person_email: user?.email || '',
          person_name: personName, department: dept, row_data: rowData,
        }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setEntries(prev => [...prev, created]);
      toast.success('Row added');
    } catch { toast.error('Failed to add row'); }
  };

  const handleDeleteRow = async (id) => {
    if (!window.confirm('Delete this row?')) return;
    try {
      const res = await fetch(`${API}/report-index/entries/${id}`, { method: 'DELETE', headers: getH() });
      if (!res.ok) throw new Error();
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Row deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleAddDay = async (date) => {
    if (!date) { toast.error('Please select a date'); return; }
    if (entries.some(e => (e.row_data?.date||'') === date)) { toast.error(`Entries for ${date} already exist`); return; }
    const entriesToCreate = TIME_SLOTS.map(slot => ({
      tab: 'daily_schedule', person_name: personName, department: dept,
      row_data: { date, timeSlot: slot, dailySchedule: '', dailyWork: '' },
    }));
    try {
      const res = await fetch(`${API}/report-index/entries/bulk`, {
        method: 'POST', headers: getH(), body: JSON.stringify({ entries: entriesToCreate }),
      });
      if (!res.ok) throw new Error();
      await loadEntries();
      toast.success(`Added ${date} — all 12 time slots ready`);
    } catch { toast.error('Failed to add day'); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const email = isHRAll ? filterEmail : (user?.email || '');
      const params = new URLSearchParams({ tab: activeSubTab });
      if (email && email !== 'all') params.set('email', email);
      if (isHRAll && filterEmail === 'all' && CHANNEL_DEPTS.includes(deptFilter)) {
        params.set('dept_filter', deptFilter);
      }
      const res = await fetch(`${API}/report-index/export?${params}`, { headers: getH() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `DBK_Report_${activeSubTab}_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const handleImportClick = () => {
    if (!IMPORTABLE_TABS.includes(activeSubTab)) return;
    if (isHR && filterEmail === 'all') { toast.error('Select a specific person before importing'); return; }
    setImportConfirm({
      type: 'tab',
      onConfirm: () => { setImportConfirm(null); fileInputRef.current?.click(); },
    });
  };

  const handleBulkDelete = async () => {
    const isAll = isHR && filterEmail === 'all';
    const target = isAll ? 'ALL employees' : (hrPeople.find(p=>p.email===filterEmail)?.label || filterEmail);
    const tabLabel = SUB_TABS.find(t=>t.id===activeSubTab)?.label || activeSubTab;
    if (!window.confirm(`DELETE ALL rows in "${tabLabel}" for ${target}?\n\nThis cannot be undone.`)) return;
    try {
      const params = new URLSearchParams({ tab: activeSubTab });
      if (!isAll && filterEmail) params.set('email', filterEmail);
      const res = await fetch(`${API}/report-index/bulk-delete?${params}`, { method: 'DELETE', headers: getH() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} rows`);
      await loadEntries();
    } catch { toast.error('Bulk delete failed'); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const targetEmail = isHRAll ? filterEmail : (user?.email || '');
      const targetPerson = isHRAll ? (hrPeople.find(p=>p.email===filterEmail)?.label?.toUpperCase()||'') : personName;
      const fd = new FormData();
      fd.append('tab', activeSubTab); fd.append('person_email', targetEmail);
      fd.append('person_name', targetPerson); fd.append('department', dept || 'MARKETING');
      fd.append('replace', 'true'); fd.append('file', file);
      const res = await fetch(`${API}/report-index/import-excel`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }, body: fd,
      });
      if (!res.ok) { const err = await res.json().catch(()=>({detail:'Import failed'})); throw new Error(err.detail); }
      const data = await res.json();
      toast.success(`Imported ${data.imported} rows from Excel`);
      await loadEntries();
    } catch (err) { toast.error(err.message || 'Import failed'); }
    finally { setImporting(false); }
  };

  const handleWbImportClick = () => {
    if (isHRAll && filterEmail === 'all') { toast.error('Select a specific person before importing'); return; }
    wbFileRef.current?.click();
  };

  // ── Phase 1: file selected → call preview endpoint ──
  const handleWbFileChange = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/report-index/preview-workbook`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Preview failed' }));
        toast.error(err.detail || 'Could not read Excel file. Check the format.');
        return;
      }
      const data = await res.json();
      setImportPreview({ file, previewData: data });
    } catch (err) {
      toast.error('Could not read Excel file. Please check the format.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Phase 2: user confirms preview → actual upload ──
  const handleConfirmImport = async () => {
    if (!importPreview) return;
    const { file } = importPreview;
    setImportPreview(null);
    setImportingWb(true);
    try {
      const targetEmail = isHRAll ? filterEmail : (user?.email || '');
      const targetPerson = isHRAll ? (hrPeople.find(p => p.email === filterEmail)?.label?.toUpperCase() || '') : personName;
      const fd = new FormData();
      fd.append('person_email', targetEmail);
      fd.append('person_name', targetPerson);
      fd.append('department', dept || 'GENERAL');
      fd.append('replace', 'true');
      fd.append('file', file);
      const res = await fetch(`${API}/report-index/import-workbook`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: fd,
      });
      if (!res.ok) { const err = await res.json().catch(() => ({ detail: 'Import failed' })); throw new Error(err.detail); }
      const data = await res.json();
      const found   = data.found_sheets  || [];
      const missing = data.missing_tabs  || [];
      if (found.length > 0) {
        toast.success(
          `Import started — ${data.total} rows queued!\n` +
          found.map(s => `✅ ${s.expected}: ${s.rows} rows`).join('\n') +
          (missing.length > 0 ? `\n❌ Not found: ${missing.join(', ')}` : ''),
          { duration: 8000 }
        );
      } else {
        toast.success(`Import started — ${data.total} rows queued. Data will appear shortly.`);
      }
      if (missing.length > 0) {
        toast.warning(`${missing.length} sheet(s) not found in your file: ${missing.join(', ')}. See the Sheet Name Guide (ℹ button) for correct names.`, { duration: 10000 });
      }
      // Poll every 3s (up to 8 times = 24s) to pick up background-task inserts
      let polls = 0;
      const poll = setInterval(async () => {
        polls++;
        await loadEntries();
        if (polls >= 8) clearInterval(poll);
      }, 3000);
    } catch (err) { toast.error(err.message || 'Workbook import failed'); }
    finally { setImportingWb(false); }
  };


  const banner = TAB_BANNERS[activeSubTab];
  const activeST = banner ? { hdr: banner.bg, hdrFg: banner.fg } : { hdr: '#1F3864', hdrFg: '#fff' };
  const showFilterBar = FILTERABLE_TABS.includes(activeSubTab);

  const filteredEntries = (() => {
    if (!showFilterBar) return entries;
    const q = searchText.trim().toLowerCase();
    const fromDt = dateFrom ? parseExcelDate(dateFrom) : null;
    const toDt = dateTo ? parseExcelDate(dateTo) : null;
    return entries.filter(e => {
      const rd = e.row_data || {};
      if (statusFilter && (rd.status||'').toString().trim().toUpperCase() !== statusFilter) return false;
      if (fromDt || toDt) {
        const rowDt = parseExcelDate(rd.date);
        if (!rowDt) return false;
        if (fromDt && rowDt < fromDt) return false;
        if (toDt && rowDt > toDt) return false;
      }
      if (q) {
        const haystack = [rd.workDetails, rd.remark, rd.person, rd.contactPerson, rd.project, rd.department]
          .map(v => (v||'').toString().toLowerCase()).join(' ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  })();

  const hasActiveFilter = showFilterBar && (searchText || statusFilter || dateFrom || dateTo);

  // Build banner title with dept
  const bannerTitle = activeSubTab === 'index1'
    ? `${dept || 'DEPARTMENT'} INDEX-1`
    : activeSubTab === 'index2'
    ? `${dept || 'DEPARTMENT'} INDEX-2`
    : banner?.title;

  return (
    <>
    <div className="space-y-4" data-testid="report-index-tab">
      <div className="flex flex-wrap items-center gap-3 rounded-xl px-5 py-3" style={{background:'linear-gradient(135deg,#1F3864 0%,#2c5282 100%)'}}>
        <div className="flex-1">
          <p className="text-white font-bold text-sm">Index 1 &amp; 2</p>
          <p className="text-blue-200 text-xs mt-0.5">
            {isHRAll ? 'HR Management View — All Departments' : `${personName} · ${dept}`}
            <span className="ml-2 text-blue-300">· Click any cell to edit directly</span>
          </p>
        </div>
        {/* ── HR Mode Toggle (Self / All Employees) ── */}
        {isHR && (
          <div className="flex rounded-lg overflow-hidden border border-blue-300" data-testid="hr-view-toggle">
            <button onClick={() => { setHrView('self'); setFilterEmail(user?.email || ''); }}
              data-testid="hr-view-self"
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${hrView === 'self' ? 'bg-white text-blue-800' : 'bg-transparent text-blue-100 hover:text-white'}`}>
              Self
            </button>
            <div className="w-px bg-blue-300" />
            <button onClick={() => { setHrView('all'); setFilterEmail('all'); }}
              data-testid="hr-view-all"
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${hrView === 'all' ? 'bg-white text-blue-800' : 'bg-transparent text-blue-100 hover:text-white'}`}>
              All Employees
            </button>
          </div>
        )}
        {/* ── Employee dropdown (only in All Employees mode) ── */}
        {isHRAll && (() => {
          // unique departments from the list (excluding empty/all) + channel depts
          const staffDepts = ['ALL', ...Array.from(new Set(hrPeople.filter(p=>p.dept).map(p=>p.dept))).sort()];
          const allDeptPills = [...staffDepts, ...CHANNEL_DEPTS.filter(d => !staffDepts.includes(d))];
          const isChannelDept = CHANNEL_DEPTS.includes(deptFilter);
          const filteredPeople = isChannelDept
            ? channelPeople
            : (deptFilter === 'ALL' ? hrPeople : hrPeople.filter(p => !p.dept || p.dept === deptFilter || p.email === 'all'));
          return (
            <div className="flex items-center gap-2 flex-wrap">
              {/* Department filter pills */}
              <div className="flex flex-wrap gap-1" data-testid="dept-filter-pills">
                {allDeptPills.map(d => (
                  <button key={d}
                    data-testid={`dept-filter-${d.toLowerCase()}`}
                    onClick={() => { setDeptFilter(d); setFilterEmail('all'); setChannelSearch(''); }}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all ${
                      deptFilter === d
                        ? 'bg-white text-blue-800 shadow'
                        : CHANNEL_DEPTS.includes(d)
                          ? 'bg-orange-600/60 text-orange-100 hover:bg-orange-500/70'
                          : 'bg-blue-700/50 text-blue-100 hover:bg-blue-600/70'
                    }`}>
                    {CHANNEL_DEPTS.includes(d) ? `DM-CH: ${d}` : d}
                  </button>
                ))}
              </div>
              {/* CP search input */}
              {isChannelDept && deptFilter === 'CP' && (
                <input
                  type="text"
                  value={channelSearch}
                  onChange={e => setChannelSearch(e.target.value)}
                  placeholder="Search CP by ID or name…"
                  data-testid="cp-channel-search"
                  className="text-xs border border-orange-300 rounded px-2 py-1.5 bg-white text-slate-700 w-40"
                />
              )}
              <select value={filterEmail} onChange={e => setFilterEmail(e.target.value)}
                data-testid="employee-select"
                className="text-xs border border-blue-300 rounded px-2 py-1.5 bg-white text-slate-700 max-w-[220px]">
                {isChannelDept ? (
                  loadingChannel ? (
                    <option disabled>Loading {deptFilter} accounts…</option>
                  ) : (
                    <>
                      <option value="all">— All {deptFilter} Combined —</option>
                      {filteredPeople.map((p, i) => (
                        <option key={`${p.email}-${i}`} value={p.email}>
                          {activeSubmitters.has(p.email) ? '\u2713 ' : ''}{p.label}
                        </option>
                      ))}
                    </>
                  )
                ) : (
                  filteredPeople.map((p, i) => (
                    <option key={`${p.email}-${i}`} value={p.email}>{p.label}</option>
                  ))
                )}
              </select>
            </div>
          );
        })()}
        {/* ── Import Excel + Sheet Name Guide ── */}
        <input ref={wbFileRef} type="file" accept=".xlsx,.xls" onChange={handleWbFileChange}
          style={{ display: 'none' }} data-testid="wb-import-file-input" />
        <div className="relative flex items-center gap-0" data-sheet-guide-container>
          <button onClick={handleWbImportClick} disabled={importingWb || previewLoading}
            data-testid="wb-import-btn"
            className="flex items-center gap-1.5 text-xs bg-amber-500 hover:bg-amber-600 disabled:bg-slate-400 text-white font-semibold px-3 py-1.5 rounded-l-lg transition-colors">
            <Upload className="w-3.5 h-3.5" />
            {previewLoading ? 'Reading…' : importingWb ? 'Importing…' : 'Import Excel'}
          </button>
          {/* ℹ Sheet Name Guide button */}
          <button
            onClick={() => setShowSheetGuide(v => !v)}
            data-testid="sheet-guide-btn"
            title="Sheet Name Guide"
            className="flex items-center justify-center w-7 h-[30px] bg-amber-600 hover:bg-amber-700 text-white rounded-r-lg transition-colors border-l border-amber-400"
          >
            <Info className="w-3.5 h-3.5" />
          </button>

          {/* Sheet Name Guide Popover */}
          {showSheetGuide && (
            <div
              ref={sheetGuideRef}
              data-testid="sheet-guide-popover"
              className="absolute top-full right-0 mt-2 z-50 bg-white border border-amber-200 rounded-xl shadow-2xl w-80 overflow-hidden"
            >
              <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between">
                <span className="font-bold text-sm">Expected Sheet Names</span>
                <button onClick={() => setShowSheetGuide(false)} className="hover:bg-amber-600 rounded p-0.5">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-4 py-3 space-y-1.5 text-xs">
                <p className="text-slate-500 mb-2">Name your Excel sheets exactly as shown (spacing & capitalisation flexible):</p>
                {[
                  { tab: 'Index 1 / INDEX-1',            hint: 'Main CFRM report index' },
                  { tab: 'Index 2 / INDEX-2',            hint: 'Secondary report index' },
                  { tab: 'Daily Schedule & Work',         hint: 'Daily schedule slots' },
                  { tab: 'Presenty / Attendance',         hint: 'Monthly attendance' },
                  { tab: 'Daily Work',                    hint: 'Daily work log' },
                  { tab: 'Work Problem',                  hint: 'Problem tracking' },
                  { tab: 'Suggestion',                    hint: 'Suggestions' },
                  { tab: 'Development Idea',              hint: 'Company dev ideas' },
                  { tab: 'Cost Cutting Idea',             hint: 'Cost cutting ideas' },
                  { tab: 'Senior Suggestion',             hint: 'Senior-level suggestions' },
                ].map(({ tab, hint }) => (
                  <div key={tab} className="flex items-start gap-2 py-1 border-b border-slate-100 last:border-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold text-slate-800">{tab}</span>
                      <span className="text-slate-400 ml-1">— {hint}</span>
                    </div>
                  </div>
                ))}
                <p className="text-slate-400 mt-2 text-[11px]">Tip: Prefixing your name is OK — e.g. "Hetal Index 1" is accepted.</p>
              </div>
            </div>
          )}
        </div>
        <button onClick={handleExport} disabled={exporting}
          className="flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-700 text-white font-semibold px-3 py-1.5 rounded-lg transition-colors">
          <Download className="w-3.5 h-3.5" />
          {exporting ? 'Exporting...' : 'Export Excel'}
        </button>
        {isHRAll && activeSubTab !== 'presently' && (
          <button onClick={handleBulkDelete}
            data-testid="bulk-delete-btn"
            className="flex items-center gap-1.5 text-xs bg-red-600 hover:bg-red-700 text-white font-semibold px-3 py-1.5 rounded-lg transition-colors">
            <Trash2 className="w-3.5 h-3.5" /> Delete All
          </button>
        )}
        {activeSubTab !== 'presently' && (
          <button
            onClick={handleSaveAll}
            disabled={savingAll}
            data-testid="save-all-btn"
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
              dirtyCount > 0
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-slate-600 hover:bg-slate-700 text-white opacity-70'
            }`}
          >
            <Save className="w-3.5 h-3.5" />
            {savingAll ? 'Saving…' : dirtyCount > 0 ? `Save All (${dirtyCount})` : 'Save All'}
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex flex-wrap gap-0 border-b border-slate-200 overflow-x-auto">
          {SUB_TABS.map(st => (
            <button key={st.id} onClick={() => setActiveSubTab(st.id)}
              data-testid={`report-subtab-${st.id}`}
              className={`px-3 py-2.5 text-xs font-semibold transition-all border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
                activeSubTab===st.id ? 'border-sky-600 text-sky-700 bg-sky-50' : 'border-transparent text-slate-500 hover:bg-slate-50'
              }`}>
              {st.label}
              {st.id==='presently' && <span className="text-[9px] bg-green-500 text-white rounded-full px-1 font-bold leading-tight">SYNC</span>}
            </button>
          ))}
        </div>

        <div className="p-4">
          {banner && activeSubTab !== 'presently' && (
            <div className="flex items-center justify-between rounded-t-lg px-4 py-2.5 mb-0 border border-b-0"
              style={{background: banner.bg, color: banner.fg, borderColor: banner.bg==='#FFD700'?'#cca200':'transparent'}}>
              <div className="flex items-center gap-3">
                <span className="font-extrabold text-base tracking-wide" style={{color:banner.fg,letterSpacing:'0.05em'}}>{bannerTitle}</span>
                {banner.sub && <span className="text-sm font-extrabold opacity-90 border-2 border-current px-3 py-0.5 rounded" style={{color:banner.fg}}>{banner.sub}</span>}
              </div>
              <span className="text-xs font-medium opacity-70">
                {hasActiveFilter ? `${filteredEntries.length} of ${entries.length} rows` : `${entries.length} rows`}
              </span>
            </div>
          )}

          {showFilterBar && !loading && (
            <div className="bg-slate-50 border border-b-0 border-slate-200 px-3 py-2 flex flex-wrap items-center gap-2" data-testid="report-filter-bar">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
                  placeholder="Search work details, remark, person..."
                  data-testid="report-filter-search"
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-300 rounded-md outline-none focus:ring-2 focus:ring-sky-400 bg-white" />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                data-testid="report-filter-status"
                className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none">
                <option value="">All statuses</option>
                {STATUS_OPTS.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="flex items-center gap-1.5 text-xs text-slate-600">
                <span>From</span>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  data-testid="report-filter-date-from"
                  className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700" />
                <span>To</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  data-testid="report-filter-date-to"
                  className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700" />
              </div>
              {hasActiveFilter && (
                <button onClick={() => { setSearchText(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); }}
                  data-testid="report-filter-clear"
                  className="flex items-center gap-1 text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-2.5 py-1.5 rounded-md">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-sky-600"></div>
              <span className="ml-3 text-slate-400 text-sm">Loading...</span>
            </div>
          ) : activeSubTab === 'daily_schedule' ? (
            <DailyScheduleTable
              key={`daily_schedule-${filterEmail}`}
              entries={entries}
              onSaveRow={handleSaveRow}
              onDeleteRow={handleDeleteRow}
              onAddRow={handleAddRow}
              onAddDay={handleAddDay}
              personName={personName}
              isHR={isHR}
              saveAllTrigger={saveAllTrigger}
              onDirtyCountChange={setDirtyCount}
              hdrColor={activeST?.hdr || '#7B3F00'}
            />
          ) : activeSubTab === 'presently' ? (
            <div className="-mx-4 -mb-4">
              <MonthlyAttendanceTab user={user} isHR={isHR} />
            </div>
          ) : (
            <SpreadsheetTable
              key={`${activeSubTab}-${filterEmail}`}
              tab={activeSubTab}
              entries={showFilterBar ? filteredEntries : entries}
              onSaveRow={handleSaveRow}
              onDeleteRow={handleDeleteRow}
              onAddRow={handleAddRow}
              personName={personName}
              dept={dept}
              isHR={isHR}
              saveAllTrigger={saveAllTrigger}
              onDirtyCountChange={setDirtyCount}
              hdrColor={activeST?.hdr || '#1F3864'}
              hdrFg={activeST?.hdrFg || '#fff'}
            />
          )}
        </div>
      </div>
    </div>

    {/* ── Import Preview Modal ── */}
    {importPreview && (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="import-preview-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
          {/* Header */}
          <div className="bg-amber-500 text-white px-6 py-4 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-base">Import Preview</h3>
              <p className="text-amber-100 text-xs mt-0.5">{importPreview.file.name}</p>
            </div>
            <button onClick={() => setImportPreview(null)} className="hover:bg-amber-600 rounded p-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Sheet detection summary */}
          <div className="px-5 pt-4 pb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Sheets detected in your file: {importPreview.previewData.wb_sheets?.join(', ')}
            </p>
          </div>

          {/* Tab-by-tab preview */}
          <div className="px-5 pb-4 max-h-72 overflow-y-auto">
            <div className="space-y-1.5">
              {(importPreview.previewData.tabs || []).map(t => (
                <div
                  key={t.tab}
                  data-testid={`preview-tab-${t.tab}`}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                    t.found && t.rows > 0
                      ? 'bg-green-50 border border-green-200'
                      : t.found && t.rows === 0
                      ? 'bg-yellow-50 border border-yellow-200'
                      : 'bg-slate-50 border border-slate-200 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {t.found && t.rows > 0
                      ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                      : t.found
                      ? <AlertCircle className="w-4 h-4 text-yellow-500 shrink-0" />
                      : <X className="w-4 h-4 text-slate-400 shrink-0" />
                    }
                    <div>
                      <span className="font-semibold text-slate-800">{t.expected}</span>
                      {t.sheet && t.sheet !== t.expected && (
                        <span className="text-slate-400 text-xs ml-1">(matched as "{t.sheet}")</span>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs font-bold ${t.rows > 0 ? 'text-green-700' : 'text-slate-400'}`}>
                    {t.found ? `${t.rows} rows` : 'Not found'}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-slate-500 bg-slate-50 rounded-lg p-2.5 border">
              Total: <span className="font-bold text-slate-700">{importPreview.previewData.total} rows</span> will be imported. Existing data in matched tabs will be replaced.
            </div>
          </div>

          {/* Actions */}
          <div className="px-5 pb-5 flex gap-3 justify-end border-t pt-4">
            <button onClick={() => setImportPreview(null)} data-testid="preview-cancel-btn"
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
              Cancel
            </button>
            <button onClick={handleConfirmImport} data-testid="preview-confirm-btn"
              disabled={importPreview.previewData.total === 0}
              className="px-5 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 disabled:bg-slate-400 rounded-lg transition-colors flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Confirm Import ({importPreview.previewData.total} rows)
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Import Confirmation Modal (replaces browser window.confirm) ── */}
    {importConfirm && (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="import-confirm-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-base">
                {importConfirm.type === 'workbook' ? 'Import Full Workbook?' : 'Import This Tab?'}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                {importConfirm.type === 'workbook'
                  ? 'This will replace your existing data in ALL tabs listed below.'
                  : 'This will replace all rows in the current tab only.'}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            {importConfirm.type === 'workbook' ? (
              <>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Tabs that will be replaced:</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {['Index 1','Index 2','Daily Schedule Work','Daily Work','Work Problem','Suggestion','Company Development Idea','Cost Cutting','Senior Suggestion'].map(t => (
                    <div key={t} className="flex items-center gap-1.5 text-xs text-slate-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      {t}
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">Tip:</span> Your Excel sheet names should be: <span className="font-mono">INDEX-1, INDEX-2, DAILY WORK</span> etc. Names like "Index 1" or "index-1" are also accepted.
                  </p>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-600">All existing rows in this tab will be permanently removed and replaced with the rows from your Excel file.</p>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 pb-5 flex gap-3 justify-end">
            <button
              data-testid="import-confirm-cancel"
              onClick={() => setImportConfirm(null)}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
              Cancel
            </button>
            <button
              data-testid="import-confirm-ok"
              onClick={importConfirm.onConfirm}
              className="px-5 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Yes, Import &amp; Replace
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ReportIndexTab;
